/**
 * Loginscherm (US-001, design-handoff scherm 1).
 *
 * De knop stuurt naar `/api/auth/login`, waarna de server de Entra-flow start. Buiten productie
 * is er daarnaast een ontwikkelinlog; die is alleen zichtbaar wanneer de server hem aanbiedt.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Beeldmerk, MicrosoftMark } from '../components/Beeldmerk.js';
import styles from './AuthScreens.module.css';

/** Vertaling van de foutcode in de URL naar een melding voor de gebruiker. */
const LOGIN_ERRORS: Record<string, string> = {
  'geen-toegang':
    'Uw Microsoft-account is geldig, maar dit e-mailadres staat niet op de toegangslijst. Vraag een beheerder om toegang.',
  mislukt: 'De aanmelding is afgebroken of mislukt. Probeer het opnieuw.',
  'niet-ingericht':
    'Microsoft Entra is voor deze omgeving nog niet ingericht. Neem contact op met een beheerder.',
};

export function LoginScreen({
  devLoginAvailable,
}: {
  devLoginAvailable: boolean;
}): React.JSX.Element {
  const [params] = useSearchParams();
  const [devEmail, setDevEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const foutcode = params.get('fout');
  const foutmelding = foutcode ? (LOGIN_ERRORS[foutcode] ?? LOGIN_ERRORS['mislukt']) : null;

  function startEntraLogin(): void {
    setBusy(true);
    window.location.href = '/api/auth/login';
  }

  function startDevLogin(event: React.FormEvent): void {
    event.preventDefault();
    setBusy(true);
    // De server antwoordt met een redirect; `response.url` bevat de bestemming nadat fetch die
    // heeft gevolgd. Zo komt de gebruiker op dezelfde plek uit als na de Entra-flow.
    void fetch('/api/auth/dev-login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: devEmail }),
      credentials: 'same-origin',
      redirect: 'follow',
    }).then((response) => {
      window.location.href = response.url || '/';
    });
  }

  return (
    <div className={styles.gradientPage}>
      <div className={styles.loginCard}>
        <div className={styles.header}>
          <Beeldmerk width={44} />
          <div>
            <div className={styles.title}>Prijsmonitor</div>
            <div className={styles.subtitle}>prijsmonitor.schoonderwolf.nl</div>
          </div>
        </div>

        <div className={styles.divider} />

        {foutmelding && (
          <div className={styles.error} role="alert">
            <span aria-hidden="true">⚠</span>
            <span>{foutmelding}</span>
          </div>
        )}

        <p className={styles.intro}>
          Log in met je Microsoft-werkaccount. Alleen e-mailadressen op de toegangslijst krijgen
          toegang.
        </p>

        <button
          type="button"
          className={styles.primaryButton}
          onClick={startEntraLogin}
          disabled={busy}
        >
          <MicrosoftMark />
          Inloggen met Microsoft
        </button>

        <div className={styles.footnote}>
          Heb je rechten voor één bedrijf, dan wordt dat <strong>automatisch geopend</strong>
        </div>

        {devLoginAvailable && (
          <div className={styles.devBox}>
            <div className={styles.devLabel}>Alleen voor ontwikkeling</div>
            <form className={styles.devRow} onSubmit={startDevLogin}>
              <input
                className={styles.devInput}
                type="email"
                required
                placeholder="e-mailadres van de toegangslijst"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                aria-label="E-mailadres voor de ontwikkelinlog"
              />
              <button type="submit" className={styles.devButton} disabled={busy}>
                Inloggen
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
